import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { playerService } from "../../services/api";
import tpIcon from "../../assets/images/tp-icon.png";
import {
  buildRows,
  computeSummary,
  currencySymbol,
  filterByRange,
  formatDate,
  formatTick,
  formatValue,
  niceStep,
  normalizeHistory,
} from "../../utils/playerValueHistory";

// Player Value tab — provider market-value history as chart + change table.
// Built 1:1 from the design handoff (ValueTab.dc.html / Player Value Tab.dc.html).
// Decided defaults: header delta vs. previous entry (caption says so), club
// markers = crest on the line (platform <img> pattern, never an emoji, unknown
// club renders nothing), no transfer-fee overlay.

const MINUS = "−";
const DOT = "·";

const RANGES = ["1Y", "3Y", "5Y", "All"];

const pulse = (delay) => ({
  animation: `tpPulse 1.4s ease-in-out infinite ${delay || "0s"}`,
});

const PlayerValue = ({ playerId }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  const [range, setRange] = useState("All");
  const [hoverIdx, setHoverIdx] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Measured container width drives every breakpoint (summary grid, chart
  // geometry, mobile card list). <640px is the declared mobile mode: summary
  // stacks to one column, table becomes stacked cards, tooltip moves to a
  // reserved slot below the plot (tap), chips stay on one row.
  const shellRef = useRef(null);
  const [shellW, setShellW] = useState(1200);
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!el) return undefined;
    const measure = () => setShellW(el.clientWidth || 1200);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchHistory = async () => {
      if (!playerId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await playerService.getPlayerMarketValue(playerId);
        if (!alive) return;
        if (response.success) {
          setPayload(response.data || { updated_at: null, history: [] });
        } else {
          setError(response.error || "Failed to fetch market value history");
          setPayload(null);
        }
      } catch (err) {
        console.error("Error fetching market value history:", err);
        if (alive) {
          setError("Failed to fetch market value history");
          setPayload(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchHistory();
    return () => {
      alive = false;
    };
  }, [playerId, fetchKey]);

  const series = useMemo(() => normalizeHistory(payload), [payload]);
  const summary = useMemo(() => computeSummary(series), [series]);
  const rows = useMemo(() => buildRows(series), [series]);

  const isMobile = shellW < 640;
  const currency = series.length
    ? series[series.length - 1].currency || "EUR"
    : "EUR";
  const fmt = useCallback((v) => formatValue(v, currency), [currency]);

  const contentW = Math.min(shellW, 1760);
  const cardPad = isMobile ? 16 : 24;

  const shown = useMemo(() => filterByRange(series, range), [series, range]);

  const singlePoint = series.length === 1;
  const showChart = series.length >= 2;
  const rangeSparse = showChart && shown.length < 2;
  const chartDrawn = showChart && shown.length >= 2;

  // ---- chart geometry (mirrors the handoff exactly) ----
  const chart = useMemo(() => {
    if (!chartDrawn) return null;
    const svgW = Math.max(contentW - cardPad * 2, 200);
    const svgH =
      isMobile ? 240 : shellW >= 1760 ? 380 : shellW < 1100 ? 290 : 330;
    const L = isMobile ? 46 : 62;
    const R = 10;
    const T = 18;
    const B = 46;
    const pw = svgW - L - R;
    const ph = svgH - T - B;
    const maxV = Math.max(...shown.map((p) => p.value));
    const step = niceStep(maxV);
    const top = Math.ceil(maxV / step) * step;
    const t0 = shown[0].time;
    const t1 = shown[shown.length - 1].time;
    const xf = (t) => L + (t1 === t0 ? pw / 2 : ((t - t0) / (t1 - t0)) * pw);
    const yf = (v) => T + ph - (v / top) * ph;
    const pts = shown.map((p) => ({ x: xf(p.time), y: yf(p.value), p }));

    const yTicks = [];
    for (let v = 0; v <= top + 1; v += step) {
      yTicks.push({ y: yf(v), ty: yf(v) + 4, label: formatTick(v, currency) });
    }

    const labelCount = isMobile ? 4 : pw > 900 ? 8 : 6;
    const stepIdx = Math.max(1, Math.round((pts.length - 1) / (labelCount - 1)));
    const xLabels = [];
    for (let i = 0; i < pts.length; i += stepIdx) {
      xLabels.push({
        x: pts[i].x,
        y1: T + ph + 22,
        y2: T + ph + 38,
        date: formatDate(pts[i].p.time, isMobile ? "year" : "short"),
        age: pts[i].p.age != null ? String(pts[i].p.age) : "",
      });
    }

    const line = pts
      .map((q, i) => (i ? "L" : "M") + q.x.toFixed(1) + " " + q.y.toFixed(1))
      .join(" ");
    const area =
      line +
      " L" + pts[pts.length - 1].x.toFixed(1) + " " + (T + ph) +
      " L" + pts[0].x.toFixed(1) + " " + (T + ph) + " Z";

    // Club-change markers: crest chip on the data point. A team without a
    // resolvable crest renders NOTHING — no placeholder, no glyph.
    const mSize = isMobile ? 20 : 24;
    const markers = [];
    pts.forEach((q, i) => {
      if (i === 0) return;
      const prevKey = pts[i - 1].p.team_id || "";
      const curKey = q.p.team_id || "";
      if (prevKey === curKey) return;
      if (!q.p.team_logo || !q.p.team_name) return;
      markers.push({
        x: q.x,
        y: q.y,
        size: mSize,
        name: q.p.team_name,
        logo: q.p.team_logo,
      });
    });

    let peakPt = pts[0];
    pts.forEach((q) => {
      if (q.p.value > peakPt.p.value) peakPt = q;
    });

    return {
      svgW, svgH, L, R, T, B, pw, ph,
      pts, yTicks, xLabels, line, area, markers,
      peak: { x: peakPt.x, y: peakPt.y },
    };
  }, [chartDrawn, shown, contentW, cardPad, isMobile, shellW, currency]);

  // ---- hover / tap tooltip ----
  const onMove = useCallback(
    (e) => {
      if (!chart) return;
      const box = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - box.left;
      let best = 0;
      let bd = Infinity;
      chart.pts.forEach((q, i) => {
        const d = Math.abs(q.x - x);
        if (d < bd) {
          bd = d;
          best = i;
        }
      });
      setHoverIdx((cur) => (cur === best ? cur : best));
    },
    [chart]
  );
  const onLeave = useCallback(() => {
    if (!isMobile) setHoverIdx(null);
  }, [isMobile]);

  useEffect(() => {
    setHoverIdx(null);
  }, [range, playerId]);

  const hover = chart && hoverIdx != null && chart.pts[hoverIdx]
    ? chart.pts[hoverIdx]
    : null;

  let tip = null;
  if (chart && hover) {
    const hp = hover.p;
    const hasClub = !!(hp.team_name || hp.team_logo);
    const tipW = isMobile ? Math.min(chart.svgW, 300) : 258;
    const tipH = 148;
    // Desktop: park the card in the plot quadrant OPPOSITE the hovered point
    // so it never covers the line; mobile: pinned slot below the plot.
    const left = isMobile
      ? Math.max(0, (chart.svgW - tipW) / 2)
      : hover.x > chart.L + chart.pw / 2
        ? chart.L + 8
        : Math.max(0, chart.L + chart.pw - tipW - 8);
    const top = isMobile
      ? chart.T + chart.ph + 52
      : hover.y < chart.T + chart.ph / 2
        ? Math.max(chart.T + 8, chart.T + chart.ph - tipH - 8)
        : chart.T + 8;
    tip = {
      left, top, tipW, hasClub,
      date: formatDate(hp.time),
      value: fmt(hp.value),
      club: hp.team_name || "",
      logo: hp.team_logo || null,
      age: hp.age != null ? String(hp.age) : null,
    };
  }

  // ---- delta pill ----
  const dir = summary ? summary.direction : null;
  const deltaColor = dir === "up" ? "#15803D" : dir === "down" ? "#B91C1C" : "#475569";
  const deltaBg = dir === "up" ? "#F0FDF4" : dir === "down" ? "#FEF2F2" : "#F8FAFC";
  const sign = dir === "up" ? "+" : dir === "down" ? MINUS : "";
  const deltaText = !summary
    ? ""
    : dir === "first"
      ? "First recorded value"
      : dir === "flat"
        ? "Unchanged"
        : sign + fmt(Math.abs(summary.deltaValue)) + "  " + sign +
          Math.abs(summary.deltaPct).toFixed(1) + "%";
  const deltaCaption =
    summary && summary.prev
      ? `vs. previous entry ${DOT} ${formatDate(summary.prev.time)}`
      : "";

  const visibleRows = expanded ? rows : rows.slice(0, 8);
  const canToggle = rows.length > 8;

  const gridCols = "1.1fr .9fr 1.1fr 1.3fr .5fr";

  const rowDelta = (r) => {
    if (r.delta == null) return null;
    const col = r.delta > 0 ? "#15803D" : r.delta < 0 ? "#B91C1C" : "#475569";
    const text =
      r.delta === 0
        ? "▬ 0.0%"
        : (r.delta > 0 ? "▲ +" : "▼ " + MINUS) +
          fmt(Math.abs(r.delta)) + "  " +
          (r.delta > 0 ? "+" : MINUS) +
          Math.abs(r.pct == null ? 0 : r.pct).toFixed(1) + "%";
    return (
      <span style={{ fontSize: 13, fontWeight: 600, color: col, whiteSpace: "nowrap" }}>
        {text}
      </span>
    );
  };

  const clubCell = (p, crestPx) =>
    p.team_name || p.team_logo ? (
      <>
        {p.team_logo && (
          <img
            src={p.team_logo}
            alt={p.team_name || ""}
            className="object-contain rounded-full flex-shrink-0"
            style={{ width: crestPx, height: crestPx }}
          />
        )}
        <span
          style={{
            fontSize: isMobile ? 13 : 14,
            color: "#334155",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {p.team_name || ""}
        </span>
      </>
    ) : null;

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: "#64748B",
  };
  const statStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    paddingLeft: isMobile ? 0 : 24,
    borderLeft: isMobile ? "0" : "1px solid #E2E8F0",
  };

  const footnote = (
    <div
      style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: "#94A3B8",
        borderTop: "1px solid #EDF1F6",
        paddingTop: 16,
      }}
    >
      Market values are provider data and reflect the provider's estimate at
      each point in time. They are not a TransferPitch valuation.
    </div>
  );

  // ================= render =================
  return (
    <div
      ref={shellRef}
      style={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: 1760,
        margin: "0 auto",
        padding: (isMobile ? "20px 0 32px" : "28px 0 40px"),
        color: "#0F172A",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <style>{`@keyframes tpPulse { 0%,100% { opacity: 1 } 50% { opacity: .45 } }`}</style>

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
              gap: 24,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ height: 11, width: 96, borderRadius: 3, background: "#E2E8F0", ...pulse() }} />
              <div style={{ height: 40, width: 180, borderRadius: 6, background: "#E2E8F0", ...pulse(".1s") }} />
              <div style={{ height: 22, width: 120, borderRadius: 11, background: "#EDF1F6", ...pulse(".2s") }} />
            </div>
            {[".15s", ".2s", ".25s"].map((d, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ height: 11, width: 96 - i * 12, borderRadius: 3, background: "#E2E8F0", ...pulse(d) }} />
                <div style={{ height: 22, width: 130 - i * 10, borderRadius: 4, background: "#E2E8F0", ...pulse(d) }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ height: 32, width: 52, borderRadius: 16, background: i ? "#EDF1F6" : "#E2E8F0", ...pulse(`.${i}s`) }} />
            ))}
          </div>
          <div
            style={{
              height: isMobile ? 220 : shellW >= 1760 ? 380 : 320,
              borderRadius: 10,
              background: "linear-gradient(180deg,#F1F5F9,#F8FAFC)",
              border: "1px solid #E2E8F0",
              ...pulse(),
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ height: 44, background: "#F8FAFC" }} />
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ height: 52, background: "#FFFFFF", borderTop: "1px solid #EDF1F6", ...pulse(`.${i}s`) }} />
            ))}
          </div>
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 14,
            padding: "88px 24px",
            border: "1px solid #E2E8F0",
            borderRadius: 10,
            background: "#FCFDFE",
          }}
        >
          <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
            <circle cx="13" cy="13" r="11" fill="none" stroke="#94A3B8" strokeWidth="1.5" />
            <path d="M13 7.5v7.5" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="13" cy="18.4" r="1.2" fill="#64748B" />
          </svg>
          <div style={{ fontSize: 17, fontWeight: 600 }}>Market value data unavailable</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: "#475569", maxWidth: 420 }}>
            The value history could not be loaded for this player. This does not
            mean the player has no recorded values.
          </div>
          <button
            type="button"
            onClick={() => setFetchKey((k) => k + 1)}
            className="hover:bg-[#0B5FCB]"
            style={{
              marginTop: 6,
              padding: "10px 18px",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 600,
              color: "#FFFFFF",
              background: "#2183FE",
              border: 0,
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && series.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 12,
            padding: "96px 24px",
            border: "1px dashed #CBD5E1",
            borderRadius: 10,
          }}
        >
          <svg width="30" height="22" viewBox="0 0 30 22" aria-hidden="true">
            <path d="M1 21h28" stroke="#CBD5E1" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M1 1v20" stroke="#CBD5E1" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M5 16h4v5H5zM12 12h4v9h-4zM19 17h4v4h-4z" fill="#EDF1F6" />
          </svg>
          <div style={{ fontSize: 17, fontWeight: 600 }}>No market value history</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: "#475569", maxWidth: 430 }}>
            No value has been recorded for this player by the data provider yet.
            Values appear here as soon as the provider publishes them.
          </div>
          <div style={{ marginTop: 14, fontSize: 12, lineHeight: 1.5, color: "#94A3B8", maxWidth: 430 }}>
            Market values are provider data and not a TransferPitch valuation.
          </div>
        </div>
      )}

      {!loading && !error && series.length > 0 && summary && (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {/* Summary header */}
          <div
            style={{
              display: "grid",
              gap: isMobile ? 22 : 32,
              gridTemplateColumns: isMobile
                ? "1fr"
                : shellW < 1024
                  ? "minmax(200px,1fr) 1fr 1fr"
                  : "minmax(240px,1.2fr) 1fr 1fr 1fr",
              alignItems: "start",
              paddingBottom: isMobile ? 4 : 8,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={labelStyle}>Current value</div>
              <div
                style={{
                  fontSize: isMobile ? 38 : shellW >= 1760 ? 52 : 44,
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                }}
              >
                {fmt(summary.last.value)}
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  alignSelf: "flex-start",
                  padding: "5px 11px",
                  borderRadius: 15,
                  background: deltaBg,
                  color: deltaColor,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {dir === "up" && (
                  <svg width="9" height="8" viewBox="0 0 9 8" aria-hidden="true">
                    <path d="M4.5 0L9 8H0z" fill="#15803D" />
                  </svg>
                )}
                {dir === "down" && (
                  <svg width="9" height="8" viewBox="0 0 9 8" aria-hidden="true">
                    <path d="M4.5 8L0 0h9z" fill="#B91C1C" />
                  </svg>
                )}
                {(dir === "flat" || dir === "first") && (
                  <svg width="9" height="8" viewBox="0 0 9 8" aria-hidden="true">
                    <rect x="0" y="3" width="9" height="2" fill="#475569" />
                  </svg>
                )}
                <span>{deltaText}</span>
              </div>
              {deltaCaption && (
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{deltaCaption}</div>
              )}
            </div>

            <div style={statStyle}>
              <div style={labelStyle}>Peak value</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{fmt(summary.peak.value)}</div>
              <div style={{ fontSize: 13, color: "#475569" }}>
                {formatDate(summary.peak.time)}
                {summary.peak.age != null ? ` ${DOT} age ${summary.peak.age}` : ""}
              </div>
            </div>

            <div style={statStyle}>
              <div style={labelStyle}>First recorded</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{fmt(summary.first.value)}</div>
              <div style={{ fontSize: 13, color: "#475569" }}>
                {formatDate(summary.first.time)}
                {summary.first.age != null ? ` ${DOT} age ${summary.first.age}` : ""}
              </div>
            </div>

            <div style={statStyle}>
              <div style={labelStyle}>Last updated</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{formatDate(summary.last.time)}</div>
              <div style={{ fontSize: 13, color: "#475569" }}>Provider record</div>
            </div>
          </div>

          {/* Chart section */}
          {showChart && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: 6 }}>
                  {RANGES.map((label) => {
                    const active = label === range;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setRange(label)}
                        style={{
                          padding: "7px 15px",
                          fontFamily: "inherit",
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                          color: active ? "#FFFFFF" : "#475569",
                          background: active ? "#0F172A" : "#FFFFFF",
                          border: active ? "1px solid #0F172A" : "1px solid #CBD5E1",
                          borderRadius: 16,
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    fontSize: 12,
                    color: "#64748B",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: "1.5px solid #CBD5E1",
                        background: "#FFFFFF",
                      }}
                    />
                    <span>Club change</span>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                      <circle cx="6" cy="6" r="5" fill="none" stroke="#2183FE" strokeWidth="1.6" />
                      <circle cx="6" cy="6" r="2" fill="#2183FE" />
                    </svg>
                    <span>Peak</span>
                  </span>
                </div>
              </div>

              <div
                style={{
                  position: "relative",
                  boxSizing: "border-box",
                  padding: cardPad,
                  border: "1px solid #E2E8F0",
                  borderRadius: 10,
                  background: "#FFFFFF",
                }}
              >
                {rangeSparse && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      alignItems: "center",
                      textAlign: "center",
                      justifyContent: "center",
                      height: isMobile ? 220 : 300,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>
                      {range} contains {shown.length === 1 ? "a single entry" : "no entries"}
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, color: "#64748B", maxWidth: 360 }}>
                      A line needs at least two entries. The full series is
                      available under{" "}
                      <span style={{ color: "#334155", fontWeight: 600 }}>All</span>.
                    </div>
                  </div>
                )}

                {chartDrawn && chart && (
                  <div
                    style={{
                      position: "relative",
                      width: chart.svgW,
                      maxWidth: "100%",
                      // Mobile reserves the pinned-tooltip slot inside the card
                      // so nothing jumps and nothing overlaps the table.
                      paddingBottom: isMobile ? 156 : 0,
                    }}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    onClick={onMove}
                  >
                    <svg
                      width={chart.svgW}
                      height={chart.svgH}
                      viewBox={`0 0 ${chart.svgW} ${chart.svgH}`}
                      style={{ display: "block", overflow: "visible" }}
                    >
                      <defs>
                        <linearGradient id="tpValueArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#2183FE" stopOpacity="0.20" />
                          <stop offset="100%" stopColor="#2183FE" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {chart.yTicks.map((t, i) => (
                        <g key={i}>
                          <line x1={chart.L} y1={t.y} x2={chart.L + chart.pw} y2={t.y} stroke="#EDF1F6" strokeWidth="1" />
                          <text x={chart.L - 10} y={t.ty} textAnchor="end" fontSize="11" fill="#94A3B8">
                            {t.label}
                          </text>
                        </g>
                      ))}
                      <path d={chart.area} fill="url(#tpValueArea)" />
                      <path
                        d={chart.line}
                        fill="none"
                        stroke="#2183FE"
                        strokeWidth="2.25"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                      {chart.xLabels.map((l, i) => (
                        <g key={i}>
                          <text x={l.x} y={l.y1} textAnchor="middle" fontSize="11" fill="#64748B">
                            {l.date}
                          </text>
                          <text x={l.x} y={l.y2} textAnchor="middle" fontSize="10" fill="#94A3B8">
                            {l.age}
                          </text>
                        </g>
                      ))}
                      <text
                        x={chart.L - 10}
                        y={chart.T + chart.ph + 38}
                        textAnchor="end"
                        fontSize="10"
                        fill="#94A3B8"
                      >
                        Age
                      </text>
                      {chart.pts.map((q, i) => (
                        <circle key={i} cx={q.x} cy={q.y} r="2.5" fill="#FFFFFF" stroke="#2183FE" strokeWidth="1.4" />
                      ))}
                      <circle cx={chart.peak.x} cy={chart.peak.y} r="6.5" fill="#FFFFFF" stroke="#2183FE" strokeWidth="1.8" />
                      <circle cx={chart.peak.x} cy={chart.peak.y} r="2.6" fill="#2183FE" />
                      {hover && (
                        <g>
                          <line
                            x1={hover.x}
                            y1={chart.T}
                            x2={hover.x}
                            y2={chart.T + chart.ph}
                            stroke="#94A3B8"
                            strokeWidth="1"
                            strokeDasharray="3 3"
                          />
                          <circle cx={hover.x} cy={hover.y} r="4.5" fill="#2183FE" stroke="#FFFFFF" strokeWidth="2" />
                        </g>
                      )}
                    </svg>

                    {/* Club-change crest markers (platform <img> pattern) */}
                    {chart.markers.map((m, i) => (
                      <div
                        key={i}
                        title={m.name}
                        style={{
                          position: "absolute",
                          left: m.x - m.size / 2,
                          top: m.y - m.size / 2,
                          width: m.size,
                          height: m.size,
                          borderRadius: "50%",
                          background: "#FFFFFF",
                          border: "1.5px solid #CBD5E1",
                          boxShadow: "0 1px 3px rgba(15,23,42,.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <img
                          src={m.logo}
                          alt={m.name}
                          className="object-contain rounded-full"
                          style={{ width: m.size - 6, height: m.size - 6 }}
                        />
                      </div>
                    ))}

                    {tip && (
                      <div
                        style={{
                          position: "absolute",
                          left: tip.left,
                          top: tip.top,
                          width: tip.tipW,
                          boxSizing: "border-box",
                          padding: "12px 14px",
                          borderRadius: 8,
                          background: "#2183FE",
                          boxShadow: "0 8px 24px rgba(33,131,254,.30)",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <span style={{ fontSize: 12, color: "#BFD0F7" }}>{tip.date}</span>
                          <img
                            src={tpIcon}
                            alt="TransferPitch"
                            width="26"
                            height="26"
                            style={{
                              display: "block",
                              width: 26,
                              height: 26,
                              borderRadius: 6,
                              background: "#FFFFFF",
                              flex: "none",
                            }}
                          />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
                          {tip.logo && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 44,
                                height: 44,
                                borderRadius: 8,
                                background: "#FFFFFF",
                                flex: "none",
                              }}
                            >
                              <img
                                src={tip.logo}
                                alt={tip.club}
                                className="object-contain"
                                style={{ width: 34, height: 34 }}
                              />
                            </span>
                          )}
                          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                            <span style={{ fontSize: 22, fontWeight: 600, color: "#FFFFFF", lineHeight: 1.1 }}>
                              {tip.value}
                            </span>
                            {tip.hasClub && (
                              <span
                                style={{
                                  fontSize: 13,
                                  color: "#E8EEFC",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {tip.club}
                              </span>
                            )}
                          </span>
                        </div>
                        {tip.age != null && (
                          <div style={{ fontSize: 12, color: "#BFD0F7", marginTop: 9 }}>
                            Age {tip.age}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Single data point: no chart, no empty axes */}
          {singlePoint && (
            <div
              style={{
                padding: "18px 20px",
                borderTop: "1px solid #E2E8F0",
                borderBottom: "1px solid #E2E8F0",
                fontSize: 13,
                lineHeight: 1.55,
                color: "#475569",
              }}
            >
              One recorded entry only. No chart is drawn until a second value is
              published.
            </div>
          )}

          {/* Value changes table */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Value changes</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>
                {rows.length === 1 ? "1 entry" : `${rows.length} entries ${DOT} newest first`}
              </div>
            </div>

            {isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {visibleRows.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      padding: "14px 16px",
                      border: "1px solid #E2E8F0",
                      borderRadius: 9,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <span style={{ fontSize: 19, fontWeight: 600 }}>{fmt(r.entry.value)}</span>
                      {rowDelta(r)}
                    </div>
                    {(r.entry.team_name || r.entry.team_logo) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {clubCell(r.entry, 20)}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 14, fontSize: 12, color: "#64748B" }}>
                      <span>{formatDate(r.entry.time)}</span>
                      {r.entry.age != null && <span>Age {r.entry.age}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 9, overflow: "hidden" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: gridCols,
                    gap: 16,
                    alignItems: "center",
                    padding: "11px 18px",
                    background: "#F8FAFC",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    color: "#64748B",
                  }}
                >
                  <div>Date</div>
                  <div style={{ textAlign: "right" }}>Value</div>
                  <div style={{ textAlign: "right" }}>Δ</div>
                  <div>Club</div>
                  <div style={{ textAlign: "right" }}>Age</div>
                </div>
                {visibleRows.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: gridCols,
                      gap: 16,
                      alignItems: "center",
                      padding: "13px 18px",
                      borderTop: "1px solid #EDF1F6",
                      background: i % 2 ? "#FCFDFE" : "#FFFFFF",
                    }}
                  >
                    <div style={{ fontSize: 14, color: "#334155" }}>{formatDate(r.entry.time)}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, textAlign: "right" }}>
                      {fmt(r.entry.value)}
                    </div>
                    <div style={{ textAlign: "right" }}>{rowDelta(r)}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                      {clubCell(r.entry, 20)}
                    </div>
                    <div style={{ fontSize: 14, color: "#475569", textAlign: "right" }}>
                      {r.entry.age != null ? r.entry.age : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {canToggle && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="hover:border-[#2183FE] hover:bg-[#F8FAFC]"
                style={{
                  alignSelf: "flex-start",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 16px",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#2183FE",
                  background: "#FFFFFF",
                  border: "1px solid #CBD5E1",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                <span>{expanded ? "Show fewer" : `Show all ${rows.length} entries`}</span>
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  aria-hidden="true"
                  style={{ transform: expanded ? "rotate(180deg)" : "none" }}
                >
                  <path d="M1 1l4 4 4-4" fill="none" stroke="#2183FE" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>

          {footnote}
        </div>
      )}
    </div>
  );
};

export default PlayerValue;
